import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useLazyQuery } from '@apollo/client';
import { VendorOpportunity } from 'modules/vendorOpportunity/types';
import { insuranceActions, insuranceSelector } from '../model';
import { GET_CREDIT_SUBMISSIONS, GET_FINANCE_PROGRAMS, Q_DOCS, QUERY_TASKS } from '../api';
import { Document, Proposal, Reference, ReferenceType, Task } from '@trnsact/trnsact-shared-types';
import { documentsByKey, taskRegardingValues } from 'pages/Prequal/constants';
import { useEffect } from 'react';
import { useStepperState } from './useStepperState';
import { coverageStatusConfig } from '../constants';
import { CoverageStatusLabels } from '../types';
import { QUERY_PROPOSALS } from 'queries/common';
import { ProposalsTableDataVariables } from 'modules/desking/api/types';
import { rejectDisabledFinancePrograms } from 'modules/financeProgram';
import { GET_INTEGRATION_SETTINGS_VALUES_BY_NAME } from 'modules/integrationSettings/api';

export function useFetchInsuranceData(vo: VendorOpportunity, creditApp: any) {
  const dispatch = useDispatch();

  const [isReady, setIsReady] = useState(false);

  const account = useSelector((state: any) => state.account);
  const lenderProfiles = useSelector((state: any) => state.lp);

  const submissions = useSelector(insuranceSelector.submissions);
  const proposals = useSelector(insuranceSelector.proposals);
  const financePrograms = useSelector(insuranceSelector.financePrograms);
  const insuranceVerificationTask = useSelector(insuranceSelector.insuranceVerificationTask);
  const insuranceBroker = useSelector(insuranceSelector.insuranceBroker);
  const insuranceCertificate = useSelector(insuranceSelector.insuranceCertificate);
  const isInsuranceExpressIntegrated = useSelector(insuranceSelector.isInsuranceExpressIntegrated);

  useEffect(() => {
    dispatch(insuranceActions.setVendorOpportunity(vo));
    dispatch(insuranceActions.setCreditApplication(creditApp));
  }, [dispatch, vo, creditApp]);

  const {
    stepperCompleteDetermineLenderRequirements,
    stepperCompleteAgentInformationNeeded,
    stepperCompleteVerifyCoverage,
  } = useStepperState();

  const { loading: isCreditSubmissionsLoading } = useQuery(GET_CREDIT_SUBMISSIONS, {
    variables: {
      id: vo.dynamicsVendorOpportunityId,
    },
    onCompleted: creditSubmissionsResponse => {
      dispatch(
        insuranceActions.setSubmissions(
          creditSubmissionsResponse?.creditSubmissionsByDynamicsVOId?.filter(Boolean) || []
        )
      );
    },
  });

  const [fetchProposals, { loading: isProposalsLoading }] = useLazyQuery<
    { proposals: Proposal[] },
    ProposalsTableDataVariables
  >(QUERY_PROPOSALS, {
    variables: {
      voId: vo.vendorOpportunityId,
    },
    onCompleted: proposalsResponse => {
      const proposals = proposalsResponse.proposals || [];
      dispatch(
        insuranceActions.setProposals(
          proposals
            .filter(p => {
              // @ts-expect-error Avoiding short circuiting error
              const isInsuranceExpress = p.lenderPaymentOptions[0].paymentCriteria?.menu?.menuOptions?.find(mo => {
                return mo?.products.find((product: any) => {
                  return (
                    product?.aftermarketProduct?.config?.productConfig?.GAIG_QUOTE !== undefined ||
                    product?.aftermarketProduct?.config?.productConfig?.GAIGQUOTE !== undefined
                  );
                });
              });
              return isInsuranceExpress !== undefined;
            })
            .map(p => ({ ...p, id: p.id || '' }))
        )
      );
    },
  });

  const { loading: isISVLoading } = useQuery(GET_INTEGRATION_SETTINGS_VALUES_BY_NAME, {
    variables: { input: { integrationName: 'GAIG', accountId: account.id } },
    fetchPolicy: 'no-cache',
    context: { authRequired: true },
    onCompleted: isvResponse => {
      dispatch(insuranceActions.setIsInsuranceExpressIntegrated(!!isvResponse?.integrationSettingsValues));
    },
    onError: (e: any) => {
      dispatch(insuranceActions.setIsInsuranceExpressIntegrated(false));
    },
  });

  useEffect(() => {
    fetchProposals();
  }, []);

  const { loading: isFinanceProgramsLoading } = useQuery(GET_FINANCE_PROGRAMS, {
    variables: {
      accountId: account.id,
      lenderProfileIds: lenderProfiles?.filter((profile: any) => profile.id).map((lp: any) => lp.id) || [],
    },
    onCompleted: financeProgramsResponse => {
      dispatch(
        insuranceActions.setFinancePrograms(
          rejectDisabledFinancePrograms(financeProgramsResponse?.financePrograms, lenderProfiles) || []
        )
      );

      const insuranceBroker = creditApp?.references?.find(
        (r: Reference) => r.referenceType === ReferenceType.Insurance
      );
      dispatch(insuranceActions.setInsuranceBroker(insuranceBroker || null));
    },
  });

  const { loading: isTasksLoading } = useQuery(QUERY_TASKS, {
    variables: { VOId: vo.vendorOpportunityId },
    onCompleted(tasksResponse) {
      const insuranceVerificationTask = tasksResponse?.vendorOpportunityTask?.find(
        (t: Task) => t.regarding === taskRegardingValues.insuranceVerification
      );
      dispatch(insuranceActions.setInsuranceVerificationTask(insuranceVerificationTask || null));
    },
  });

  const { loading: isDocumentsLoading } = useQuery(Q_DOCS, {
    variables: { VOId: vo.vendorOpportunityId },
    onCompleted(docsResponse) {
      const insuranceCertificate = docsResponse?.documents?.find(
        (d: Document) => d.docType === documentsByKey.insurance_certificate.value
      );
      dispatch(insuranceActions.setInsuranceCertificate(insuranceCertificate || null));
    },
  });

  useEffect(() => {
    const isNeedToSetSubmissionAndProgram =
      insuranceVerificationTask?.financeProgramId && submissions.length && financePrograms.length;

    if (isNeedToSetSubmissionAndProgram) {
      dispatch(
        insuranceActions.setSelectedSubmission(
          submissions.find(cs => cs.creditSubId === insuranceVerificationTask.creditSubId)
        )
      );
      dispatch(
        insuranceActions.setSelectedFinanceProgram(
          financePrograms.find(fp => fp.financeProgramId === insuranceVerificationTask.financeProgramId)
        )
      );
      stepperCompleteDetermineLenderRequirements();
      if (insuranceBroker) {
        stepperCompleteAgentInformationNeeded();
        dispatch(
          insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredUnverified])
        );
      }
    }

    if (insuranceCertificate) {
      stepperCompleteDetermineLenderRequirements();
      stepperCompleteAgentInformationNeeded();
      stepperCompleteVerifyCoverage();
      dispatch(insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredVerified]));
    }
  }, [insuranceVerificationTask, submissions, proposals, financePrograms, insuranceBroker, insuranceCertificate]);

  return {
    isFetching:
      isCreditSubmissionsLoading ||
      isProposalsLoading ||
      isFinanceProgramsLoading ||
      isTasksLoading ||
      isDocumentsLoading ||
      isInsuranceExpressIntegrated === null,
  };
}
